'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { Menu, X, Phone } from 'lucide-react';

const navLinks = [
  { label: 'Trips', href: '/trips-listing-page' },
  { label: 'About', href: '#about' },
  { label: 'Contact', href: '#contact' },
];

export default function PublicHeader() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-sm shadow-nav' : 'bg-transparent'
      }`}
    >
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link href="/homepage" className="flex items-center gap-2.5">
            <AppLogo size={36} />
            <span className={`font-display text-xl font-bold tracking-tight transition-colors ${scrolled ? 'text-[#1A3C5E]' : 'text-white'}`}>
              VinciAdventures
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks?.map((link) => (
              <Link
                key={`nav-${link?.label}`}
                href={link?.href}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-150 hover:bg-white/10 ${
                  scrolled ? 'text-gray-700 hover:text-[#E8440A] hover:bg-orange-50' : 'text-white/90 hover:text-white'
                }`}
              >
                {link?.label}
              </Link>
            ))}
          </nav>

          {/* Actions */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="tel:+916381503933"
              className={`flex items-center gap-1.5 text-sm font-medium transition-colors ${
                scrolled ? 'text-gray-600 hover:text-[#E8440A]' : 'text-white/80 hover:text-white'
              }`}
            >
              <Phone size={14} />
              +91 6381503933
            </a>
            <Link href="/trips-listing-page" className="btn-primary text-sm px-5 py-2.5">
              Book a Trip
            </Link>
          </div>

          {/* Mobile Hamburger */}
          <button
            className={`md:hidden p-2 rounded-lg transition-colors ${scrolled ? 'text-gray-700' : 'text-white'}`}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle mobile menu"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      {/* Mobile Drawer */}
      <div
        className={`md:hidden transition-all duration-300 overflow-hidden ${
          mobileOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
        } bg-white border-t border-gray-100 shadow-lg`}
      >
        <div className="px-6 py-4 flex flex-col gap-1">
          {navLinks?.map((link) => (
            <Link
              key={`mobile-nav-${link?.label}`}
              href={link?.href}
              className="px-4 py-3 rounded-xl text-gray-700 font-semibold hover:bg-orange-50 hover:text-[#E8440A] transition-all"
              onClick={() => setMobileOpen(false)}
            >
              {link?.label}
            </Link>
          ))}
          <div className="mt-3 pt-3 border-t border-gray-100">
            <Link
              href="/trips-listing-page"
              className="btn-primary w-full justify-center"
              onClick={() => setMobileOpen(false)}
            >
              Book a Trip
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
}